"use client";

import * as React from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Replaces `pre` in MDX articles: the fenced block as written, plus a
 * copy button in the corner. The text copied is the block's rendered
 * text, so a one-line address copies without its trailing newline.
 */
export function CodeBlock({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLPreElement>) {
  const preRef = React.useRef<HTMLPreElement>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    // textContent rather than innerText: it is what jsdom implements and
    // a <pre> renders its text content verbatim anyway.
    const text = preRef.current?.textContent?.trim() ?? "";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard unavailable (insecure context, denied permission):
      // the text is still selectable by hand.
    }
  };

  return (
    <div className="not-prose group relative my-8">
      <pre
        ref={preRef}
        className={cn(
          "bg-muted border-border overflow-x-auto rounded-xl border p-6 pr-14 font-mono text-sm shadow-sm",
          className,
        )}
        {...rest}
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy to clipboard"}
        title={copied ? "Copied" : "Copy"}
        className="bg-background/80 text-muted-foreground hover:text-foreground border-border absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-md border shadow-sm backdrop-blur transition-colors"
      >
        {copied ? (
          <Check className="size-4" aria-hidden="true" />
        ) : (
          <Copy className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
